import { EditOutlined, ZoomInOutlined } from '@ant-design/icons';
import { Button, Table, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { CellEditPage, RowDetailsPage } from '.';
import { Field, Grid, GridCell, GridRow } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';
import { CellStatusIndicator } from './cell-status-indicator';
import { GridCellContent } from './grid-cell';
import { FloatingModal } from '../../elements';

export function RecordRevisionsTable(props: { grid?: Grid; gridRow?: GridRow; onRowAdded: () => void }) {
  const { Text } = Typography;

  const [rowsLoading, setRowsLoading] = useState(false);
  const [tableColumns, setTableColumns] = useState<any[]>();
  const [rows, setRows] = useState<GridRow[]>();
  const [cellDetailsModalOpen, setCellDetailsModalOpen] = useState<any>();
  const [rowDetailsModalOpen, setRowDetailsModalOpen] = useState<any>();
  const [showTasks, setShowTasks] = useState(false);
  const [hasActiveOperation, setHasActiveOperation] = useState(false);

  const loadRecords = useCallback(() => {
    setRowsLoading(true);

    const body: any = {
      gridId: props.grid?.id,
      revisionsFor: [props.gridRow?.latestRevisionRecordId],
      limit: 0,
    };

    if (showTasks) {
      body.includeTasks = true;
    }

    apiClient
      .post(`records/records`, body)
      .then((response: any) => {
        setRows(response.data.records);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setRowsLoading(false));
  }, [props.grid?.id, props.gridRow?.latestRevisionRecordId, showTasks]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const onCellSaved = useCallback(
    (row: GridRow) => {
      if (!rows) {
        return;
      }

      const newRows = [...rows];
      const index = newRows.findIndex(r => r.id === row.id);

      if (index < 0) {
        return;
      }

      newRows[index] = row;
      setRows(newRows);
    },
    [rows],
  );

  const onSubGridChanged = loadRecords;

  const openCellDetailsModal = useCallback(
    (field: Field, rowId: string) => {
      setCellDetailsModalOpen({ row: props.gridRow, gridId: field.gridId, field, rowId });
    },
    [props.gridRow],
  );

  useEffect(() => {
    if (!props.grid) {
      return;
    }

    const columns: any[] = [];

    for (let i = 0; i < props.grid.fields.length; i++) {
      const field = props.grid.fields[i];
      const columnWidth = (field.displayName || field.name).length * 10 + 30;

      const column = {
        title: field.displayName || field.name,
        dataIndex: ['cells', field.id],
        key: field.id,
        fixed: field.isIdentifier ? 'left' : undefined,
        width: columnWidth < 150 ? 150 : columnWidth,
        className: 'ant-table-cell-align-top',
        render: (cell: GridCell, row: GridRow) => (
          <GridCellContent
            grid={props.grid}
            field={field}
            rowId={row.id}
            cell={cell}
            isObsolete={row.isObsolete}
            isEllipsis={true}
            showTasks={showTasks}
            hasActiveOperation={hasActiveOperation}
            onOperationStarted={onOperationStarted}
            onOperationEnded={onOperationEnded}
            onCellClick={openCellDetailsModal}
            onSaved={onCellSaved}
            onSubGridChanged={onSubGridChanged}
          />
        ),
      };
      if (field.isIdentifier) {
        columns.unshift(column);
      } else {
        columns.push(column);
      }
    }

    const idColumn = {
      title: '',
      dataIndex: 'id',
      width: 80,
      key: 'id',
      fixed: 'left',
      className: 'ant-table-cell-align-top',
      render: (id: string, row: GridRow) => {
        return (
          <div style={{ display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-between' }}>
            <div>
              <Button type="default" shape="circle" size="small" icon={<ZoomInOutlined />} onClick={() => openRowDetailsModal(row)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {row.valueTaskCount > 0 && <CellStatusIndicator color="gold" count={row.valueTaskCount} />}
              {Object.values(row.cells).some(c => c.errors?.length) && (
                <CellStatusIndicator color="red" count={Object.values(row.cells).reduce((acc, c) => acc + (c.errors?.length || 0), 0)} />
              )}
            </div>
          </div>
        );
      },
    };

    columns.unshift(idColumn);

    if (props.grid?.isRevision) {
      const revisionCommentColumn = {
        title: 'Revizyon açıklaması',
        dataIndex: 'revisionComment',
        key: 'revisionComment',
        fixed: 'left',
        width: 200,
        className: 'ant-table-cell-align-top',
        render: (revisionComment: string, row: GridRow) => {
          return <Text>{revisionComment}</Text>;
        },
      };

      columns.unshift(revisionCommentColumn);
    }

    setTableColumns(columns);
  }, [Text, hasActiveOperation, onCellSaved, onSubGridChanged, openCellDetailsModal, props.grid, showTasks]);

  const openRowDetailsModal = (row: GridRow) => {
    setRowDetailsModalOpen(row);
  };

  const closeRowDetailsModal = () => {
    setRowDetailsModalOpen(undefined);
  };

  const onRowSaved = (row: GridRow) => {
    if (!rows) {
      return;
    }

    const newRows = [...rows];
    const index = newRows.findIndex(r => r.id === row.id);

    if (index < 0) {
      return;
    }

    const rowWithKey = { ...row, key: row.id };
    newRows[index] = rowWithKey;
    setRows(newRows);
  };

  const onRowDelete = () => {
    closeRowDetailsModal();
    loadRecords();
  };

  const closeCellDetailsModal = () => {
    setCellDetailsModalOpen(undefined);
  };

  const onShowTasksClick = () => {
    setShowTasks(!showTasks);
  };

  const onOperationStarted = () => {
    setHasActiveOperation(true);
  };

  const onOperationEnded = () => {
    setHasActiveOperation(false);
  };

  return (
    <>
      <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
        <Button onClick={onShowTasksClick} icon={<EditOutlined />}>
          {showTasks ? 'Düzenlemeyi kapat' : 'Düzenle'}
        </Button>
      </div>
      <Table
        columns={tableColumns}
        dataSource={rows}
        loading={rowsLoading}
        scroll={{ x: '100%', y: 'calc(100vh - 360px' }}
        pagination={false}
        rowKey={(row: GridRow) => row.id}
        size="small"
        bordered
        className="mdm-grid"
        rowClassName={(row: GridRow) => (row.id === props.gridRow?.id ? 'mdm-grid-row-highlight' : '')}
      />
      <FloatingModal
        title="Kayıt detayları"
        visible={rowDetailsModalOpen}
        footer={null}
        onCancel={closeRowDetailsModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <RowDetailsPage
          grid={props.grid}
          row={rowDetailsModalOpen}
          onRowAdded={props.onRowAdded}
          onSaved={onRowSaved}
          onDelete={() => onRowDelete}
        />
      </FloatingModal>
      <FloatingModal
        title="Veri detayları"
        visible={cellDetailsModalOpen}
        footer={null}
        onCancel={closeCellDetailsModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <CellEditPage
          record={cellDetailsModalOpen?.row}
          grid={props.grid}
          gridId={cellDetailsModalOpen?.gridId}
          rowId={cellDetailsModalOpen?.rowId}
          field={cellDetailsModalOpen?.field}
          allowRecordWithErrors={props.grid?.allowRecordWithErrors}
          onSaved={onCellSaved}
        />
      </FloatingModal>
    </>
  );
}
